import { useCallback, useState } from 'react';
import { FormFieldState, UploadedFile } from '../../../../core/components';
import {
  DescriptionFormValues,
  MAX_FILES,
  descriptionFormSchema,
  getFileError,
} from '../../types/descriptionForm';
import { useTouchedFormState } from '../useTouchedFormState/useTouchedFormState';

export type DescriptionFieldName = 'description';

const INITIAL_VALUES: DescriptionFormValues = { description: '' };

function generateFileId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

type UseDescriptionFormResult = {
  values: DescriptionFormValues;
  files: UploadedFile[];
  isValid: boolean;
  handleDescriptionChange: (value: string) => void;
  handleBlur: (field: DescriptionFieldName) => void;
  getFieldState: (field: DescriptionFieldName) => FormFieldState;
  getFieldError: (field: DescriptionFieldName) => string | undefined;
  handleAddFiles: (fileList: FileList) => void;
  handleRemoveFile: (id: string) => void;
  fileUploadError: string | undefined;
};

export function useDescriptionForm(): UseDescriptionFormResult {
  const { values, setValues, isValid, handleBlur, getFieldState, getFieldError } =
    useTouchedFormState<DescriptionFormValues>({
      schema: descriptionFormSchema,
      initialValues: INITIAL_VALUES,
    });
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [fileUploadError, setFileUploadError] = useState<string>();

  const handleDescriptionChange = useCallback(
    (value: string) => {
      setValues({ description: value });
    },
    [setValues],
  );

  const handleAddFiles = useCallback(
    (fileList: FileList) => {
      const accepted: UploadedFile[] = [];
      let error: string | undefined;

      for (const file of Array.from(fileList)) {
        if (files.length + accepted.length >= MAX_FILES) {
          error = 'Limite atingido. Remova um arquivo para adicionar outro.';
          break;
        }
        const fileError = getFileError(file);
        if (fileError) {
          error = fileError;
          continue;
        }
        accepted.push({ id: generateFileId(), file });
      }

      if (accepted.length > 0) {
        setFiles((previous) => [...previous, ...accepted]);
      }
      setFileUploadError(error);
    },
    [files.length],
  );

  const handleRemoveFile = useCallback((id: string) => {
    setFiles((previous) => previous.filter((uploaded) => uploaded.id !== id));
    setFileUploadError(undefined);
  }, []);

  return {
    values,
    files,
    isValid,
    handleDescriptionChange,
    handleBlur,
    getFieldState,
    getFieldError,
    handleAddFiles,
    handleRemoveFile,
    fileUploadError,
  };
}
